/**
 * Interactive Building Facade Component
 * Displays the front-facing building render with clickable unit polygons
 */
import { useState, useEffect, useCallback } from 'react';
import { getAllUnits, type PropertyUnit } from '@/lib/units';
import { getFrontFacingPolygons, getExteriorRenderConfig, type FrontFacingPolygon, type ExteriorRenderConfig, type BlockBViewId } from '@/lib/front-facing-coordinates';
import type { UnitFilters } from '@/types/map';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import type { InteractiveMapProps } from '@/types/map';
import { formatFloorDisplay, getStatusColorHex } from '@/lib/utils';
import { trackUnitClick } from '@/lib/unit-interest';
import { getFilterLabel } from '@/lib/filter-validation';
import { Building2, Eye, EyeOff, Filter, X, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';

interface InteractiveBuildingFacadeProps extends InteractiveMapProps {
  block?: 'A' | 'B';
  onBlockChange?: (block: 'A' | 'B') => void;
  onViewFilterChange?: (filter: string) => void;
  onAvailabilityFilterChange?: (filter: string) => void;
  onCategoryFilterChange?: (filter: string) => void;
  onFloorFilterChange?: (filter: string) => void;
  // Show units toggle
  showUnits?: boolean;
  onShowUnitsChange?: (show: boolean) => void;
}

export function InteractiveBuildingFacade({ 
  onUnitClick, 
  selectedUnit,
  viewFilter = 'all',
  availabilityFilter = 'all',
  categoryFilter = 'all',
  floorFilter = 'all',
  block: initialBlock = 'A',
  onBlockChange,
  onViewFilterChange,
  onAvailabilityFilterChange,
  onCategoryFilterChange,
  onFloorFilterChange,
  showUnits: controlledShowUnits,
  onShowUnitsChange,
}: InteractiveBuildingFacadeProps) {
  const [units, setUnits] = useState<PropertyUnit[]>([]);
  const [frontFacingPolygons, setFrontFacingPolygons] = useState<FrontFacingPolygon[]>([]);
  const [polygonUnitsMap, setPolygonUnitsMap] = useState<Map<string, PropertyUnit>>(new Map());
  const [hoveredUnit, setHoveredUnit] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBlock, setSelectedBlock] = useState<'A' | 'B'>(initialBlock);
  const [renderConfig, setRenderConfig] = useState<ExteriorRenderConfig | null>(null);
  const [singleImageDimensions, setSingleImageDimensions] = useState<{ width: number; height: number } | null>(null);
  const [multiImageDimensions, setMultiImageDimensions] = useState<Map<string, { width: number; height: number }>>(new Map());
  const [selectedView, setSelectedView] = useState<BlockBViewId>('center');
  const [internalViewFilter, setInternalViewFilter] = useState(viewFilter);
  const [internalAvailabilityFilter, setInternalAvailabilityFilter] = useState(availabilityFilter);
  const [internalCategoryFilter, setInternalCategoryFilter] = useState(categoryFilter);
  const [internalFloorFilter, setInternalFloorFilter] = useState(floorFilter);
  const [internalShowUnits, setInternalShowUnits] = useState(controlledShowUnits ?? false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Use controlled value if provided, otherwise use internal state
  const showUnits = controlledShowUnits !== undefined ? controlledShowUnits : internalShowUnits;

  const handleShowUnitsChange = (show: boolean) => {
    if (onShowUnitsChange) {
      onShowUnitsChange(show);
    } else {
      setInternalShowUnits(show);
    }
  };

  // Use controlled values if callbacks provided, otherwise use internal state
  const activeViewFilter = onViewFilterChange ? viewFilter : internalViewFilter;
  const activeAvailabilityFilter = onAvailabilityFilterChange ? availabilityFilter : internalAvailabilityFilter;
  const activeCategoryFilter = onCategoryFilterChange ? categoryFilter : internalCategoryFilter;
  const activeFloorFilter = onFloorFilterChange ? floorFilter : internalFloorFilter;

  // Filter change handlers
  const handleViewFilterChange = (filter: string) => {
    if (onViewFilterChange) {
      onViewFilterChange(filter);
    } else {
      setInternalViewFilter(filter);
    }
  };

  const handleAvailabilityFilterChange = (filter: string) => {
    if (onAvailabilityFilterChange) {
      onAvailabilityFilterChange(filter);
    } else {
      setInternalAvailabilityFilter(filter);
    }
  };

  const handleCategoryFilterChange = (filter: string) => {
    if (onCategoryFilterChange) {
      onCategoryFilterChange(filter);
    } else {
      setInternalCategoryFilter(filter);
    }
  };

  const handleFloorFilterChange = (filter: string) => {
    if (onFloorFilterChange) {
      onFloorFilterChange(filter);
    } else {
      setInternalFloorFilter(filter);
    }
  };

  // Load exterior render config when block changes (single image or multi-image for Block B)
  useEffect(() => {
    let cancelled = false;
    async function loadConfig() {
      const config = await getExteriorRenderConfig(selectedBlock);
      if (cancelled) return;
      setRenderConfig(config);
      if (config?.type === 'single' && config.url) {
        setMultiImageDimensions(new Map());
        const img = new Image();
        img.onload = () => {
          if (!cancelled) setSingleImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
        };
        img.src = config.url;
      } else {
        setSingleImageDimensions(null);
      }
    }
    loadConfig();
    return () => { cancelled = true; };
  }, [selectedBlock]);

  // Load actual image dimensions for multi-image (Block B) sections
  useEffect(() => {
    if (renderConfig?.type !== 'multi-image' || !renderConfig.sections.length) {
      setMultiImageDimensions(new Map());
      return;
    }
    const dimensionsMap = new Map<string, { width: number; height: number }>();
    let loadedCount = 0;
    const sections = renderConfig.sections;

    sections.forEach((section) => {
      const img = new Image();
      img.onload = () => {
        dimensionsMap.set(section.id, {
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
        loadedCount++;
        if (loadedCount === sections.length) {
          setMultiImageDimensions(new Map(dimensionsMap));
        }
      };
      img.onerror = () => {
        loadedCount++;
        if (loadedCount === sections.length) {
          setMultiImageDimensions(new Map(dimensionsMap));
        }
      };
      img.src = section.url;
    });
  }, [renderConfig]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Build filters
      const filters: UnitFilters = {};
      if (activeViewFilter !== 'all') filters.viewType = activeViewFilter;
      if (activeAvailabilityFilter !== 'all') filters.availability = activeAvailabilityFilter;
      if (activeCategoryFilter !== 'all') filters.category = activeCategoryFilter;
      if (activeFloorFilter !== 'all') filters.floor = activeFloorFilter;

      // Load units
      const { data: loadedUnits, error: unitsError } = await getAllUnits(filters);
      if (unitsError) {
        console.error('Error loading units:', unitsError);
      } else {
        setUnits(loadedUnits || []);
      }

      // Load front-facing polygons for selected block
      const { data: polygons, error: polygonsError } = await getFrontFacingPolygons(selectedBlock);
      if (polygonsError) {
        console.error('Error loading front-facing polygons:', polygonsError);
      } else if (polygons) {
        setFrontFacingPolygons(polygons);
        
        // Map polygons to units
        const unitsMap = new Map<string, PropertyUnit>();
        const loadedUnitsArray = loadedUnits || [];
        const unmatchedPolygons: string[] = [];
        
        for (const polygon of polygons) {
          // Find matching unit by unit_id
          const matchingUnit = loadedUnitsArray.find(u => u.id === polygon.unit_id);
          if (matchingUnit) {
            // Apply filters
            if (activeViewFilter !== 'all' && matchingUnit.view_type !== activeViewFilter) continue;
            if (activeAvailabilityFilter !== 'all' && matchingUnit.availability !== activeAvailabilityFilter) continue;
            if (activeCategoryFilter !== 'all' && matchingUnit.category !== activeCategoryFilter) continue;
            if (activeFloorFilter !== 'all') {
              if (activeFloorFilter === 'fourth') {
                const isPenthouse = 
                  matchingUnit.floor === 'fourth' || 
                  (matchingUnit.floor === 'third' && 
                   matchingUnit.floors_included && 
                   matchingUnit.floors_included.includes('fourth') && 
                   matchingUnit.category !== 'townhouse');
                if (!isPenthouse) continue;
              } else {
                // Inclusive: check if unit includes this floor (primary OR floors_included)
                const unitIncludesFloor = 
                  matchingUnit.floor === activeFloorFilter ||
                  (matchingUnit.floors_included?.includes(activeFloorFilter));
                if (!unitIncludesFloor) continue;
              }
            }
            unitsMap.set(polygon.id, matchingUnit);
          } else {
            unmatchedPolygons.push(`${polygon.unit_number} (unit_id: ${polygon.unit_id})`);
          }
        }
        
        setPolygonUnitsMap(unitsMap);
        console.log(`📊 Loaded ${polygons.length} front-facing polygons for Block ${selectedBlock}, ${unitsMap.size} matched to units`);
        if (unmatchedPolygons.length > 0) {
          console.warn(`⚠️ Unmatched polygons (no unit found):`, unmatchedPolygons);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedBlock, activeViewFilter, activeAvailabilityFilter, activeCategoryFilter, activeFloorFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle block change
  const handleBlockChange = (block: 'A' | 'B') => {
    setSelectedBlock(block);
    onBlockChange?.(block);
  };

  // Handle unit click
  async function handlePolygonClick(unit: PropertyUnit) {
    const contactEmail = typeof window !== 'undefined' 
      ? sessionStorage.getItem('hubspot_contact_email') || undefined
      : undefined;
    
    await trackUnitClick(unit.id, contactEmail);
    onUnitClick(unit.unit_number);
  }

  // Render a polygon
  function renderPolygon(polygon: FrontFacingPolygon) {
    const coords = polygon.map_coordinates as { type?: string; points?: number[][] } | null;
    
    if (!coords || coords.type !== 'Polygon' || !coords.points || coords.points.length < 3) {
      return null;
    }

    const unit = polygonUnitsMap.get(polygon.id);
    const hasUnit = !!unit;

    // Check if selected or hovered
    const isSelected = hasUnit && selectedUnit === unit.unit_number;
    const isHovered = hasUnit && hoveredUnit === unit.unit_number;
    
    // Determine fill color based on state
    // Default: subtle outline so polygons are always visible and hoverable
    let fillColor = 'transparent';
    let fillOpacity = 0.01; // tiny opacity to capture mouse events
    let strokeColor = hasUnit ? 'rgba(55, 65, 81, 0.3)' : 'rgba(107, 114, 128, 0.2)';
    let strokeWidth = 1;
    
    if (isSelected) {
      fillColor = '#C87A7A'; // muted red
      fillOpacity = 0.7;
      strokeColor = '#A85A5A';
      strokeWidth = 3;
    } else if (isHovered && hasUnit) {
      fillColor = '#D9A3A3'; // lighter muted red for hover
      fillOpacity = 0.6;
      strokeColor = '#C87A7A'; // muted red
      strokeWidth = 2;
    } else if (showUnits && hasUnit) {
      // Show availability color when toggle is ON
      fillColor = getStatusColorHex(unit.availability);
      fillOpacity = 0.5;
      strokeColor = '#374151';
      strokeWidth = 1;
    } else if (showUnits && !hasUnit) {
      // Show unmatched polygons with a subtle outline when Show Units is ON
      fillColor = '#9ca3af'; // gray
      fillOpacity = 0.2;
      strokeColor = '#6b7280';
      strokeWidth = 1;
    }

    // Convert points to SVG polygon string (no transformation for tabbed views)
    const pointsString = coords.points
      .map((point: number[]) => `${point[0]},${point[1]}`)
      .join(' ');

    return (
      <polygon
        key={polygon.id}
        points={pointsString}
        fill={fillColor}
        fillOpacity={fillOpacity}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeOpacity={0.8}
        className={hasUnit ? "cursor-pointer transition-all duration-200" : "cursor-default transition-all duration-200"}
        onClick={() => unit && handlePolygonClick(unit)}
        onMouseEnter={() => unit && setHoveredUnit(unit.unit_number)}
        onMouseLeave={() => setHoveredUnit(null)}
        data-polygon-id={polygon.id}
        data-unit-id={polygon.unit_id}
      >
        <title>
          {hasUnit
            ? `${unit.unit_number} (${formatFloorDisplay(unit.floor)}) - ${unit.category} - ${unit.availability}`
            : `Unit ${polygon.unit_number} - No data`}
        </title>
      </polygon>
    );
  }

  // For multi-image (tabbed views): current view's section and dimensions
  const currentSection =
    renderConfig?.type === 'multi-image' ? renderConfig.sections.find((s) => s.id === selectedView) : null;
  const currentSectionDimensions = currentSection ? multiImageDimensions.get(currentSection.id) : null;
  const currentViewWidth = currentSectionDimensions?.width ?? currentSection?.dimensions.width ?? 1920;
  const currentViewHeight = currentSectionDimensions?.height ?? currentSection?.dimensions.height ?? 960;

  // ViewBox: multi-image uses selected view's dimensions; single uses loaded image dimensions
  const viewBox =
    renderConfig?.type === 'multi-image' && currentSection
      ? `0 0 ${currentViewWidth} ${currentViewHeight}`
      : singleImageDimensions
        ? `0 0 ${singleImageDimensions.width} ${singleImageDimensions.height}`
        : '0 0 1920 960';

  const hasRender = renderConfig && (
    renderConfig.type === 'single' ? !!renderConfig.url : renderConfig.sections.length > 0
  );

  // Dynamic container aspect ratio: selected view's image (multi) or single image
  const containerAspectRatio =
    renderConfig?.type === 'multi-image' && currentSection
      ? currentViewWidth / currentViewHeight
      : singleImageDimensions
        ? singleImageDimensions.width / singleImageDimensions.height
        : 2;

  // For multi-image tabbed views: show only polygons for the selected view
  const visiblePolygons =
    renderConfig?.type === 'multi-image'
      ? frontFacingPolygons.filter((p) => p.view_type === `front-${selectedView}`)
      : frontFacingPolygons;

  const availableCount = units.filter(u => u.availability === 'available' && u.block === selectedBlock).length;
  const reservedCount = units.filter(u => u.availability === 'reserved' && u.block === selectedBlock).length;
  const soldCount = units.filter(u => u.availability === 'sold' && u.block === selectedBlock).length;

  // Calculate active filter count for mobile badge
  const activeFilterCount = [
    activeAvailabilityFilter !== 'all',
    activeFloorFilter !== 'all',
  ].filter(Boolean).length;

  // Shared filter button component for consistency
  const FilterButton = ({ 
    active, 
    onClick, 
    children 
  }: { 
    active: boolean; 
    onClick: () => void; 
    children: React.ReactNode 
  }) => (
    <Button 
      variant={active ? 'default' : 'outline'}
      size="sm"
      onClick={onClick}
      className="min-h-[44px] min-w-[44px] lg:min-h-0 lg:min-w-0"
    >
      {children}
    </Button>
  );

  return (
    <div className="space-y-6">
      {/* Block Selector and Filters */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          {/* Block Selector - Always visible */}
          <div className="flex gap-1 mr-2">
            <Button 
              variant={selectedBlock === 'A' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleBlockChange('A')}
              className="min-h-[44px] lg:min-h-0"
            >
              <Building2 className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Canal View</span>
              <span className="sm:hidden">Canal</span>
            </Button>
            <Button 
              variant={selectedBlock === 'B' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleBlockChange('B')}
              className="min-h-[44px] lg:min-h-0"
            >
              <Building2 className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Lagoon View</span>
              <span className="sm:hidden">Lagoon</span>
            </Button>
          </div>

          {/* Show Units Toggle - Always visible */}
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-md border bg-background min-h-[44px] lg:min-h-0">
            {showUnits ? (
              <Eye className="h-4 w-4 text-muted-foreground" />
            ) : (
              <EyeOff className="h-4 w-4 text-muted-foreground" />
            )}
            <Label htmlFor="show-units" className="text-sm font-medium cursor-pointer hidden sm:inline">
              Show Units
            </Label>
            <Switch
              id="show-units"
              checked={showUnits}
              onCheckedChange={handleShowUnitsChange}
            />
          </div>

          {/* Mobile Filter Trigger */}
          <Button 
            className="lg:hidden min-h-[44px]" 
            variant="outline"
            onClick={() => setFilterDrawerOpen(true)}
          >
            <Filter className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Filters</span>
            {activeFilterCount > 0 && (
              <Badge className="ml-2 bg-brand-teal text-white">{activeFilterCount}</Badge>
            )}
          </Button>

          {/* Desktop Inline Filters */}
          <div className="hidden lg:flex flex-wrap gap-2">
            {/* Availability Filter */}
            <FilterButton active={activeAvailabilityFilter === 'all'} onClick={() => handleAvailabilityFilterChange('all')}>
              All Status
            </FilterButton>
            <FilterButton active={activeAvailabilityFilter === 'available'} onClick={() => handleAvailabilityFilterChange('available')}>
              {getFilterLabel('availability', 'available')}
            </FilterButton>
            <FilterButton active={activeAvailabilityFilter === 'reserved'} onClick={() => handleAvailabilityFilterChange('reserved')}>
              {getFilterLabel('availability', 'reserved')}
            </FilterButton>
            <FilterButton active={activeAvailabilityFilter === 'sold'} onClick={() => handleAvailabilityFilterChange('sold')}>
              {getFilterLabel('availability', 'sold')}
            </FilterButton>

            {/* Floor Filter */}
            <FilterButton active={activeFloorFilter === 'all'} onClick={() => handleFloorFilterChange('all')}>
              All Floors
            </FilterButton>
            <FilterButton active={activeFloorFilter === 'GF'} onClick={() => handleFloorFilterChange('GF')}>
              {getFilterLabel('floor', 'GF')}
            </FilterButton>
            <FilterButton active={activeFloorFilter === 'second'} onClick={() => handleFloorFilterChange('second')}>
              {getFilterLabel('floor', 'second')}
            </FilterButton>
            <FilterButton active={activeFloorFilter === 'third'} onClick={() => handleFloorFilterChange('third')}>
              {getFilterLabel('floor', 'third')}
            </FilterButton>
          </div>
        </div>

        {/* Status Badges */}
        <div className="flex flex-wrap gap-2 lg:gap-4">
          <Badge className="bg-status-available text-white border-status-available">
            Available: {availableCount}
          </Badge>
          <Badge className="bg-status-reserved text-white border-status-reserved">
            Reserved: {reservedCount}
          </Badge>
          {soldCount > 0 && (
            <Badge className="bg-status-sold text-white border-status-sold">
              Sold: {soldCount}
            </Badge>
          )}
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      <Drawer open={filterDrawerOpen} onOpenChange={setFilterDrawerOpen}>
        <DrawerContent className="max-h-[85vh]">
          <DrawerHeader>
            <DrawerTitle>Filter Units</DrawerTitle>
            <DrawerDescription>
              Narrow down units by status or floor
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-4 pb-4 space-y-6 overflow-y-auto">
            {/* Availability */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-muted-foreground">Availability</h4>
              <div className="flex flex-wrap gap-2">
                <FilterButton active={activeAvailabilityFilter === 'all'} onClick={() => handleAvailabilityFilterChange('all')}>
                  All Status
                </FilterButton>
                <FilterButton active={activeAvailabilityFilter === 'available'} onClick={() => handleAvailabilityFilterChange('available')}>
                  {getFilterLabel('availability', 'available')}
                </FilterButton>
                <FilterButton active={activeAvailabilityFilter === 'reserved'} onClick={() => handleAvailabilityFilterChange('reserved')}>
                  {getFilterLabel('availability', 'reserved')}
                </FilterButton>
                <FilterButton active={activeAvailabilityFilter === 'sold'} onClick={() => handleAvailabilityFilterChange('sold')}>
                  {getFilterLabel('availability', 'sold')}
                </FilterButton>
              </div>
            </div>

            {/* Floor */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-muted-foreground">Floor</h4>
              <div className="flex flex-wrap gap-2">
                <FilterButton active={activeFloorFilter === 'all'} onClick={() => handleFloorFilterChange('all')}>
                  All Floors
                </FilterButton>
                <FilterButton active={activeFloorFilter === 'GF'} onClick={() => handleFloorFilterChange('GF')}>
                  {getFilterLabel('floor', 'GF')}
                </FilterButton>
                <FilterButton active={activeFloorFilter === 'second'} onClick={() => handleFloorFilterChange('second')}>
                  {getFilterLabel('floor', 'second')}
                </FilterButton>
                <FilterButton active={activeFloorFilter === 'third'} onClick={() => handleFloorFilterChange('third')}>
                  {getFilterLabel('floor', 'third')}
                </FilterButton>
              </div>
            </div>
          </div>
          <DrawerFooter>
            <DrawerClose asChild>
              <Button variant="outline" className="w-full min-h-[44px]">
                <X className="h-4 w-4 mr-2" />
                Close
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* View tabs (Block B multi-image only) */}
      {renderConfig?.type === 'multi-image' && (
        <Tabs value={selectedView} onValueChange={(v) => setSelectedView(v as BlockBViewId)}>
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="southside">Southside</TabsTrigger>
            <TabsTrigger value="center">Center</TabsTrigger>
            <TabsTrigger value="northside">Northside</TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {/* Building Facade View with Zoom/Pan Support */}
      <div
        className="relative w-full bg-muted rounded-lg border overflow-hidden"
        style={{ aspectRatio: containerAspectRatio }}
      >
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">Loading building view...</p>
            </div>
          </div>
        ) : !hasRender ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <Building2 className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
              <p className="text-muted-foreground">No building render available for {selectedBlock === 'A' ? 'Canal View' : 'Lagoon View'}</p>
            </div>
          </div>
        ) : (
          <TransformWrapper
            initialScale={1}
            minScale={0.5}
            maxScale={4}
            centerOnInit
            wheel={{ step: 0.1 }}
            pinch={{ step: 5 }}
            doubleClick={{ mode: 'zoomIn', step: 0.5 }}
          >
            {({ zoomIn, zoomOut, resetTransform }) => (
              <>
                {/* Zoom Controls */}
                <div className="absolute top-2 right-2 z-10 flex flex-col gap-1">
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-8 w-8 bg-background/90 backdrop-blur-sm shadow-md"
                    onClick={() => zoomIn()}
                    title="Zoom in"
                  >
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-8 w-8 bg-background/90 backdrop-blur-sm shadow-md"
                    onClick={() => zoomOut()}
                    title="Zoom out"
                  >
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-8 w-8 bg-background/90 backdrop-blur-sm shadow-md"
                    onClick={() => resetTransform()}
                    title="Reset zoom"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </div>
                
                <TransformComponent
                  wrapperClass="!w-full !h-full"
                  contentClass="!w-full !h-full"
                >
                  <svg
                    viewBox={viewBox}
                    className="w-full h-full"
                    xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="xMidYMid meet"
                  >
            {/* Background: single image or selected tab view (multi-image shows one image) */}
            {renderConfig?.type === 'single' && renderConfig.url && (
              <image
                href={renderConfig.url}
                x="0"
                y="0"
                width={singleImageDimensions?.width || 1920}
                height={singleImageDimensions?.height || 960}
                preserveAspectRatio="none"
                style={{ pointerEvents: 'none' }}
              />
            )}
            {renderConfig?.type === 'multi-image' && currentSection && (
              <image
                href={currentSection.url}
                x="0"
                y="0"
                width={currentViewWidth}
                height={currentViewHeight}
                preserveAspectRatio="xMidYMid meet"
                style={{ pointerEvents: 'none' }}
              />
            )}
            
            {/* Render polygons for current view (filtered; offset for multi-image) */}
            {visiblePolygons.map(polygon => renderPolygon(polygon))}
            
            {/* Unit labels - show when toggle is enabled, or on hover when toggle is off */}
            {visiblePolygons.map(polygon => {
              const coords = polygon.map_coordinates as any;
              if (!coords || coords.type !== 'Polygon' || !coords.points || coords.points.length === 0) {
                return null;
              }
              
              const unit = polygonUnitsMap.get(polygon.id);
              if (!unit) return null;
              
              const showLabel = showUnits || unit.unit_number === hoveredUnit || unit.unit_number === selectedUnit;
              if (!showLabel) return null;
              
              // Center point for label (no transformation for tabbed views)
              const centerX = coords.points.reduce((sum: number, p: number[]) => sum + p[0], 0) / coords.points.length;
              const centerY = coords.points.reduce((sum: number, p: number[]) => sum + p[1], 0) / coords.points.length;
              
              return (
                <text
                  key={`label-${polygon.id}`}
                  x={centerX}
                  y={centerY}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="pointer-events-none font-bold"
                  fill="#0f172a"
                  fontSize="26"
                  stroke="white"
                  strokeWidth="3"
                  paintOrder="stroke fill"
                  style={{
                    textShadow: '0 1px 2px rgba(0,0,0,0.3), 0 0 4px rgba(255,255,255,0.9)',
                  }}
                >
                  {unit.unit_number}
                </text>
              );
            })}
                  </svg>
                </TransformComponent>
              </>
            )}
          </TransformWrapper>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-status-available rounded"></div>
          <span>Available</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-status-reserved rounded"></div>
          <span>Reserved</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-status-sold rounded"></div>
          <span>Sold</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded border-2" style={{ backgroundColor: '#C87A7A', borderColor: '#A85A5A' }}></div>
          <span>Selected</span>
        </div>
      </div>
    </div>
  );
}
